/**
 * Refusés — dedicated list of candidates with status="rejected".
 *
 * These candidates are excluded from the default Pépites / "À appeler"
 * lists so they don't pollute active queues. This page lets the team
 * review them, export, or revert a decision if needed.
 *
 * Permissions: pre_recruteur, referent, admin (anyone who can rejected a
 * candidate should be able to audit their own decisions).
 */
import { motion } from "framer-motion";
import { Download, Loader2, Search, UserX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import { toE164 } from "../lib/format";


function rejectionReasonLabel(reason) {
    const map = {
        criteres_non_remplis: "Critères non remplis",
        candidat_indisponible: "Candidat indisponible",
        numero_injoignable: "Numéro injoignable",
        immersion_failed: "Échec immersion",
        immersion_no_show: "No-show immersion",
        other: "Autre",
    };
    return map[reason] || reason || "—";
}


function exportCsv(list) {
    if (!list.length) return;
    const rows = [
        ["Prénom", "Nom", "Email", "Téléphone", "Ville", "Score IA", "Motif refus", "Refusé le", "Source", "Offre"],
        ...list.map((c) => [
            c.first_name || "",
            c.last_name || "",
            c.email || "",
            toE164(c.phone) || c.phone || "",
            c.city || "",
            c.ai_score ?? "",
            rejectionReasonLabel(c.rejection_reason),
            c.rejected_at ? new Date(c.rejected_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" }) : "",
            c.source || "",
            c.layan_offer_title || "",
        ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `refuses-shariti-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}


export default function Refuses() {
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState([]);
    const [search, setSearch] = useState("");
    const [reasonFilter, setReasonFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/candidates", { params: { status: "rejected", sort: "recent" } });
                if (!cancelled) setCandidates(data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const reasons = useMemo(() => {
        const set = new Set();
        candidates.forEach((c) => { if (c.rejection_reason) set.add(c.rejection_reason); });
        return Array.from(set);
    }, [candidates]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return candidates.filter((c) => {
            if (reasonFilter !== "all" && c.rejection_reason !== reasonFilter) return false;
            if (q) {
                const blob = `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.phone || ""} ${c.city || ""}`.toLowerCase();
                if (!blob.includes(q)) return false;
            }
            return true;
        });
    }, [candidates, reasonFilter, search]);

    if (loading) {
        return (
            <div className="flex justify-center py-20" data-testid="refuses-loading">
                <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-24 md:pb-8" data-testid="refuses-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-shariti-red border border-red-200 text-[10px] font-bold uppercase tracking-widest">
                        <UserX className="h-3 w-3" /> Hors flux
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Candidats refusés
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {candidates.length} candidat{candidates.length > 1 ? "s" : ""} archivé{candidates.length > 1 ? "s" : ""} — exclus des files d'appel et des Pépites.
                    </p>
                </div>
                <button
                    onClick={() => exportCsv(filtered)}
                    disabled={!filtered.length}
                    data-testid="refuses-export"
                    className="btn-secondary disabled:opacity-40"
                >
                    <Download className="h-4 w-4" /> Exporter CSV
                </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un nom, email…"
                        data-testid="refuses-search"
                        className="w-full pl-9 pr-3 h-10 rounded-full bg-white border border-shariti-gray text-sm focus:outline-none focus:border-shariti-orange transition"
                    />
                </div>
                {reasons.length > 0 && (
                    <select
                        value={reasonFilter}
                        onChange={(e) => setReasonFilter(e.target.value)}
                        data-testid="refuses-reason-filter"
                        className="h-10 px-4 rounded-full bg-white border border-shariti-gray text-sm font-semibold text-slate-700 focus:outline-none focus:border-shariti-orange transition"
                    >
                        <option value="all">Tous les motifs</option>
                        {reasons.map((r) => (
                            <option key={r} value={r}>{rejectionReasonLabel(r)}</option>
                        ))}
                    </select>
                )}
            </div>

            {/* List */}
            {filtered.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-shariti-gray" data-testid="refuses-empty">
                    <UserX className="h-10 w-10 mx-auto text-slate-300" />
                    <p className="mt-4 font-bold text-shariti-black">Aucun candidat refusé</p>
                    <p className="mt-1 text-sm text-slate-500">
                        Les candidats que vous refusez apparaîtront ici, hors des files d'appel actives.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((c) => (
                        <motion.div
                            key={c.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-2xl border border-shariti-gray p-5 hover:shadow-md transition"
                            data-testid={`refuses-card-${c.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-11 w-11 rounded-xl bg-red-50 text-shariti-red font-extrabold text-sm flex items-center justify-center shrink-0">
                                    {c.first_name?.[0]}{c.last_name?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/candidats/${c.id}`}
                                        className="text-base font-extrabold text-shariti-black hover:text-shariti-orange transition break-words block"
                                    >
                                        {c.first_name} {c.last_name}
                                    </Link>
                                    <div className="text-xs font-semibold text-slate-500 break-words">
                                        {c.layan_offer_title || c.source || "—"}
                                    </div>
                                </div>
                                {c.ai_score > 0 && (
                                    <div className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray shrink-0">
                                        IA {c.ai_score}
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-red-50 text-shariti-red border border-red-200">
                                <UserX className="h-2.5 w-2.5" />
                                {rejectionReasonLabel(c.rejection_reason)}
                            </div>
                            {c.rejected_at && (
                                <div className="mt-2 text-[11px] font-semibold text-slate-500">
                                    Refusé le {new Date(c.rejected_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short", year: "numeric" })}
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
